import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Container,
  FormControlLabel, InputAdornment,
  Paper, Radio, RadioGroup, TextField, Typography,
} from '@mui/material'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import SearchIcon from '@mui/icons-material/Search'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import { useAuth } from '../contexts/AuthContext'
import { TURKEY_PROVINCES, canAccessEmergency } from '../data/turkeyProvinces'
import { EMERGENCY_API_READY, subscribeEmergencyHistory, submitEmergencyCall } from '../services/emergencyCalls'
import DescopeEmergencyDialog from '../components/DescopeEmergencyDialog'
import './EmergencyPage.css'

const STATUS = {
  pending: { label: 'Sonuç bekleniyor', color: 'warning' },
  accepted: { label: 'İstek kabul edildi', color: 'success' },
  rejected: { label: 'İstek reddedildi', color: 'error' },
  unknown: { label: 'Sonuç doğrulanamadı', color: 'warning' },
}
const normalizeSearch = (value) => value.toLocaleLowerCase('tr').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ı/g, 'i')
const formatDate = (value) => value?.toDate
  ? new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Istanbul' }).format(value.toDate())
  : 'Zaman kaydediliyor…'

export default function EmergencyPage() {
  const { currentUser, userStatus } = useAuth()
  const hasAccess = canAccessEmergency(currentUser, userStatus)
  const [scope, setScope] = useState('provinces')
  const [provinceCodes, setProvinceCodes] = useState([])
  const [search, setSearch] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const sendingRef = useRef(false)
  const [feedback, setFeedback] = useState(null)
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState(false)

  useEffect(() => {
    if (!hasAccess) return undefined
    return subscribeEmergencyHistory((items) => {
      setHistory(items)
      setHistoryLoading(false)
      setHistoryError(false)
    }, () => {
      setHistoryError(true)
      setHistoryLoading(false)
    })
  }, [hasAccess])

  const selectedProvinces = useMemo(() => TURKEY_PROVINCES.filter((province) => provinceCodes.includes(province.code)), [provinceCodes])
  const visibleProvinces = TURKEY_PROVINCES.filter((province) => normalizeSearch(province.name).includes(normalizeSearch(search.trim())) || province.code.includes(search.trim()))
  const scopeLabel = scope === 'nationwide' ? 'Türkiye geneli · 81 il' : `${provinceCodes.length} il seçildi`
  const validSelection = scope === 'nationwide' || provinceCodes.length > 0

  function toggleProvince(code) {
    setProvinceCodes((previous) => previous.includes(code) ? previous.filter((value) => value !== code) : [...previous, code])
  }

  async function handleSubmit(otpProof) {
    if (!hasAccess || !validSelection || sendingRef.current || !EMERGENCY_API_READY) return
    sendingRef.current = true
    setSending(true)
    setFeedback(null)
    try {
      const result = await submitEmergencyCall({ scope, provinceCodes, currentUser, otpProof })
      setFeedback({ severity: result.historyWarning || result.status === 'unknown' ? 'warning' : result.status === 'accepted' ? 'success' : 'error', message: result.message })
    } catch (error) {
      setFeedback({ severity: 'error', message: error.message || 'Çağrı başlatılamadı.' })
    } finally {
      setConfirmOpen(false)
      sendingRef.current = false
      setSending(false)
    }
  }

  if (!hasAccess) return <Container sx={{ py: 4 }}><Alert severity="error">Bu sayfaya yalnızca aktif acil durum ekibi üyeleri erişebilir.</Alert></Container>

  return (
    <Box component="main" className="emergency-page">
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <Chip icon={<CampaignOutlinedIcon />} label="Acil durum ekibi" size="small" sx={{ mb: 1.5, bgcolor: '#fce9e5', color: '#a34338', fontWeight: 700 }} />
          <Typography component="h1" sx={{ fontSize: { xs: 27, md: 34 }, fontWeight: 800, color: '#22313f' }}>Acil Durum Destek</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>Çağrının kapsamını belirleyin, destek sürecini başlatın ve ekip hareketlerini takip edin.</Typography>
        </Box>

        {!EMERGENCY_API_READY && <Alert severity="info" sx={{ mb: 2 }}>Çağrı servisi bağlantısı hazırlanıyor. İl seçimi ve Descope e-posta doğrulaması kullanılabilir; destek çağrısı henüz gönderilmeyecek.</Alert>}
        {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.message}</Alert>}

        <Box className="emergency-layout">
          <Paper variant="outlined" className="emergency-panel" sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography component="h2" variant="h6" fontWeight={700}>Çağrı kapsamı</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>Tüm Türkiye’ye veya belirlediğiniz illere destek çağrısı gönderin.</Typography>
            <RadioGroup aria-label="Çağrı kapsamı" value={scope} onChange={(event) => setScope(event.target.value)} className="emergency-scope">
              <FormControlLabel value="provinces" disabled={sending || confirmOpen} control={<Radio />} label="İl seçerek" />
              <FormControlLabel value="nationwide" disabled={sending || confirmOpen} control={<Radio />} label="Türkiye geneli (81 il)" />
            </RadioGroup>

            {scope === 'provinces' ? <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" label="İl adı veya plaka kodu ara" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ flex: '1 1 240px' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
                <Button size="small" disabled={sending || confirmOpen || provinceCodes.length === 81} onClick={() => setProvinceCodes(TURKEY_PROVINCES.map((province) => province.code))}>Tümünü seç</Button>
                <Button size="small" disabled={sending || confirmOpen || !provinceCodes.length} onClick={() => setProvinceCodes([])}>Temizle</Button>
              </Box>
              <Box className="emergency-provinces" role="group" aria-label="Türkiye illeri">
                {visibleProvinces.map((province) => (
                  <FormControlLabel key={province.code} className={`emergency-province${provinceCodes.includes(province.code) ? ' selected' : ''}`} disabled={sending || confirmOpen}
                    control={<Checkbox size="small" checked={provinceCodes.includes(province.code)} onChange={() => toggleProvince(province.code)} />}
                    label={<><span className="emergency-plate">{province.code}</span>{province.name}</>}
                  />
                ))}
              </Box>
              {!visibleProvinces.length && <Typography color="text.secondary" sx={{ py: 3 }}>Aramanızla eşleşen il bulunamadı.</Typography>}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>{visibleProvinces.length} / 81 il gösteriliyor · {provinceCodes.length} il seçildi</Typography>
            </> : <Box className="emergency-nationwide">
              <PublicOutlinedIcon sx={{ fontSize: 48, color: '#b44b4b', mb: 1 }} />
              <Typography fontWeight={700}>Çağrı tüm Türkiye’yi kapsayacak</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>81 ilin tamamı bu destek çağrısına dahil edilir.</Typography>
            </Box>}
          </Paper>

          <Paper component="aside" variant="outlined" className="emergency-panel emergency-summary" sx={{ p: 3 }}>
            <Typography component="h2" variant="h6" fontWeight={700}>Çağrı özeti</Typography>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2 }}>KAPSAM</Typography>
            <Typography fontWeight={700}>{scopeLabel}</Typography>
            {scope === 'provinces' && <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5, maxHeight: 210, overflowY: 'auto' }}>
              {selectedProvinces.map((province) => <Chip key={province.code} label={province.name} size="small" onDelete={sending || confirmOpen ? undefined : () => toggleProvince(province.code)} />)}
              {!provinceCodes.length && <Typography variant="body2" color="text.secondary">Başlatmak için soldaki listeden il seçin.</Typography>}
            </Box>}
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>BAŞLATAN</Typography>
            <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{currentUser.nickname || currentUser.email}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{currentUser.email}</Typography>
            <Button fullWidth variant="contained" color="error" startIcon={sending ? <CircularProgress color="inherit" size={18} /> : <CampaignOutlinedIcon />} disabled={!validSelection || sending || historyError || historyLoading} onClick={() => setConfirmOpen(true)} sx={{ mt: 3, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
              {sending ? 'Çağrı gönderiliyor…' : 'Destek çağrısı başlat'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Çağrı kapsamı, başlatan kişi ve zaman bilgisi ekip geçmişine kaydedilir.</Typography>
          </Paper>
        </Box>

        <Paper component="section" variant="outlined" className="emergency-panel" sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}><HistoryOutlinedIcon color="action" /><Typography component="h2" variant="h6" fontWeight={700}>Çağrı geçmişi</Typography><Button component="a" href="#/emergency/reports" sx={{ ml: 'auto', textTransform: 'none' }}>Gönderim raporunu aç</Button></Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Ekibin son 100 çağrı isteği · Saatler Türkiye saatidir.</Typography>
          {historyError ? <Alert severity="error">Çağrı geçmişi yüklenemedi. Bağlantıyı ve erişim izinlerini kontrol edin.</Alert>
            : historyLoading ? <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={26} aria-label="Geçmiş yükleniyor" /></Box>
              : !history.length ? <Box className="emergency-empty"><HistoryOutlinedIcon sx={{ fontSize: 36, color: '#8a9aa8', mb: 1 }} /><Typography fontWeight={600}>Henüz çağrı başlatılmadı</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Ekibin başlattığı destek çağrıları burada görünecek.</Typography></Box>
                : <Box className="emergency-history">{history.map((item) => {
                  const status = STATUS[item.status] || STATUS.unknown
                  return <Box component="article" className="emergency-history-row" key={item.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700}>Acil durum destek çağrısı</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{item.scope === 'nationwide' ? 'Türkiye geneli · 81 il' : (item.provinces || []).map((province) => province.name).join(', ')}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{item.initiatedByName || item.initiatedByEmail}{item.initiatedByName && item.initiatedByEmail ? ` · ${item.initiatedByEmail}` : ''}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(item.createdAt)}</Typography>
                      {item.apiResponseText && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.apiResponseText}</Typography>}
                    </Box>
                    <Box><Chip size="small" variant="outlined" label={status.label} color={status.color} />
                      {['pending', 'unknown'].includes(item.status) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 210, mt: 1 }}>Sonuç kesinleşmedi. Yeniden başlatmadan önce kontrol edin.</Typography>}
                    </Box>
                  </Box>
                })}</Box>}
        </Paper>

        <DescopeEmergencyDialog
          open={confirmOpen}
          selection={{ scope, provinceCodes }}
          uid={currentUser.uid}
          apiReady={EMERGENCY_API_READY}
          onClose={() => setConfirmOpen(false)}
          onSubmit={handleSubmit}
        />
      </Container>
    </Box>
  )
}
